<template>
  <!-- Modal backdrop -->
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isOpen"
        data-testid="shop-cart-backdrop"
        class="fixed inset-0 z-50 flex items-center justify-end bg-surface-inverse/50 backdrop-blur-sm"
        @click.self="handleClose"
      >
        <!-- Cart panel -->
        <div
          data-testid="shop-cart-panel"
          class="relative flex h-full w-[600px] flex-col overflow-hidden border-l border-main bg-surface shadow-xl"
          @click.stop
        >
          <!-- Scrollable content -->
          <div class="flex flex-col gap-4 overflow-y-auto p-8">
            <!-- Header -->
            <FestivalShopCartHeader :item-count="cartItemCount" @close="handleClose" />

            <!-- Order info -->
            <FestivalShopCartOrderInfo :kiosk-name="kioskName" :order-type="localOrderType" @update:order-type="handleOrderTypeChange" />

            <!-- Divider -->
            <div class="bg-border-main h-px w-full" />

            <!-- Cart items -->
            <div v-if="cartItems.length > 0" data-testid="shop-cart-items-list" class="flex w-full flex-col gap-0">
              <template v-for="(item, index) in cartItems" :key="`${item.productId}-${item.variantId}`">
                <FestivalShopCartItem :item="item" @update:quantity="handleUpdateQuantity" @remove="handleRemoveItem" />
                <!-- Divider between items -->
                <div v-if="index < cartItems.length - 1" class="bg-border-main my-4 h-px w-full" />
              </template>
            </div>

            <!-- Empty cart message -->
            <div v-else data-testid="shop-cart-empty" class="flex flex-col items-center justify-center py-8 text-center">
              <p class="txt-body-m text-subtle">{{ t('FestivalShop.cart.emptyCart') }}</p>
            </div>

            <!-- Divider -->
            <div v-if="cartItems.length > 0" class="bg-border-main h-px w-full" />

            <!-- Total -->
            <FestivalShopCartSummary v-if="cartItems.length > 0" :total="cartTotal" />

            <!-- Footer -->
            <FestivalShopCartFooter v-if="cartItems.length > 0" @checkout="handleCheckout" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, inject } from 'vue'
import { useI18n } from 'vue-i18n'

import { FestivalShopCartKey } from '@/composables/useFestivalShopCart'

const { t } = useI18n()

import FestivalShopCartFooter from './FestivalShopCartFooter.vue'
import FestivalShopCartHeader from './FestivalShopCartHeader.vue'
import FestivalShopCartItem from './FestivalShopCartItem.vue'
import FestivalShopCartOrderInfo from './FestivalShopCartOrderInfo.vue'
import FestivalShopCartSummary from './FestivalShopCartSummary.vue'

export interface Props {
  isOpen?: boolean
  kioskName?: string
}

withDefaults(defineProps<Props>(), {
  isOpen: false,
  kioskName: '',
})

const emit = defineEmits<{
  close: []
  checkout: [orderType: 'new' | 'existing']
}>()

// Get cart from composable
const cart = inject(FestivalShopCartKey)
if (!cart) {
  throw new Error('FestivalShopCart composable not provided')
}

const { cartItems, cartItemCount, cartTotal, updateCartItemCount, removeFromCart } = cart

// Local state for order type
const localOrderType = ref<'new' | 'existing'>('new')

function handleClose() {
  emit('close')
}

function handleOrderTypeChange(type: 'new' | 'existing') {
  localOrderType.value = type
}

function handleUpdateQuantity(productId: string, variantId: string, count: number) {
  updateCartItemCount(productId, variantId, count)
}

function handleRemoveItem(productId: string, variantId: string) {
  removeFromCart(productId, variantId)
}

function handleCheckout() {
  emit('checkout', localOrderType.value)
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity var(--motion-duration-medium) var(--motion-easing-standard);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active > div,
.modal-fade-leave-active > div {
  transition: transform var(--motion-duration-medium) var(--motion-easing-standard);
}

.modal-fade-enter-from > div {
  transform: translateX(100%);
}

.modal-fade-leave-to > div {
  transform: translateX(100%);
}
</style>
